import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Popover,
  PopoverTrigger,
  Button,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Input,
  Divider,
  VStack,
  Checkbox,
  Text,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';

export interface MultiSelectOption {
  label: string;
  value: string;
}
interface DependentQuestionMultiSelectProps {
  placeholder?: string;
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  width?: string | number;
}

export const DependentQuestionMultiSelect: React.FC<DependentQuestionMultiSelectProps> = ({
  placeholder = 'Select Dependent Questions...',
  options,
  selectedValues,
  onChange,
  width = '100%',
}) => {
  const [search, setSearch] = useState('');

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  }, [search, options]);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  return (
    <Popover placement="bottom-start" gutter={10}>
      <PopoverTrigger>
        <Button
          variant="outline"
          size="sm"
          width={width}
          justifyContent="space-between"
          rightIcon={<ChevronDownIcon />}
          fontWeight="normal"
          colorScheme={selectedValues.length > 0 ? 'purple' : 'gray'}
          bg={selectedValues.length > 0 ? 'purple.50' : 'white'}
          _hover={{ bg: selectedValues.length > 0 ? 'purple.100' : 'gray.50' }}
        >
          {selectedValues.length === 0 ? placeholder : `${selectedValues.length} selected`}
        </Button>
      </PopoverTrigger>

      <PopoverContent w="260px" p={0} zIndex={100}>
        <PopoverArrow />

        <PopoverBody p={2}>
          <Input
            size="sm"
            placeholder="Search..."
            mb={2}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <Divider mb={2} />

          <VStack align="stretch" maxH="200px" spacing={1} overflowY="auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <Checkbox
                  key={opt.value}
                  size="sm"
                  isChecked={selectedValues.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  colorScheme="purple"
                >
                  {opt.label}
                </Checkbox>
              ))
            ) : (
              <Text fontSize="xs" color="gray.500" p={2}>
                No results found.
              </Text>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
